import { useState, useEffect, useMemo } from "react";
import type {
  DictionaryStore,
  SearchMode,
  SearchResult,
  TokenizedWord,
  ResultFilter,
} from "@/lib/types";
import { initializeDictionary } from "@/lib/dictionary-store";
import { search, filterResults } from "@/lib/search";

interface UseDictionaryReturn {
  isLoading: boolean;
  error: string | null;
  entryCount: number;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  searchMode: SearchMode | null;
  tokens: TokenizedWord[] | undefined;
  results: SearchResult[];
  filteredResults: SearchResult[];
  filter: ResultFilter;
  setFilter: (filter: ResultFilter) => void;
  jyutpingCount: number;
  englishCount: number;
}

function getInitialQuery(): string {
  if (typeof window === "undefined") return "";
  const params = new URLSearchParams(window.location.search);
  return params.get("q") ?? "";
}

export function useDictionary(): UseDictionaryReturn {
  const [store, setStore] = useState<DictionaryStore | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState(getInitialQuery);
  const [filter, setFilter] = useState<ResultFilter>("all");

  // Sync search query to URL
  useEffect(() => {
    const url = new URL(window.location.href);
    if (searchQuery.trim()) {
      url.searchParams.set("q", searchQuery);
    } else {
      url.searchParams.delete("q");
    }
    window.history.replaceState({}, "", url.toString());
  }, [searchQuery]);

  // Load dictionary on mount
  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const dictionaryStore = await initializeDictionary();
        if (!cancelled) {
          setStore(dictionaryStore);
          setIsLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load dictionary");
          setIsLoading(false);
        }
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  // Memoized search results
  const searchResults = useMemo(() => {
    if (!store || !searchQuery.trim()) {
      return { mode: null, tokens: undefined, results: [] };
    }

    const result = search(searchQuery, store);
    return {
      mode: result.mode,
      tokens: result.tokens,
      results: result.results,
    };
  }, [store, searchQuery]);

  // Count results by type
  const { jyutpingCount, englishCount } = useMemo(() => {
    const results = searchResults.results;
    return {
      jyutpingCount: results.filter((r) => r.matchType === "jyutping").length,
      englishCount: results.filter((r) => r.matchType === "english").length,
    };
  }, [searchResults.results]);

  // Filtered results
  const filteredResults = useMemo(() => {
    return filterResults(searchResults.results, filter);
  }, [searchResults.results, filter]);

  return {
    isLoading,
    error,
    entryCount: store?.entries.length ?? 0,
    searchQuery,
    setSearchQuery,
    searchMode: searchResults.mode,
    tokens: searchResults.tokens,
    results: searchResults.results,
    filteredResults,
    filter,
    setFilter,
    jyutpingCount,
    englishCount,
  };
}
